import React, { useCallback, useEffect, useState } from 'react';
import { requestJson } from '../utils/authClient.js';
import { AdminSection, ShowMore, fill, useShowMore } from '../components/AdminNav.jsx';
import VeridionOrganisationPanel from './VeridionOrganisationPanel.jsx';
import { AdminLink, FactGrid, displayModelName, formatBerlin, formatUsd } from './VeridionShared.jsx';

// The operator surface of Veridion Core inside the company tab: the Core's state and emergency
// stop, the agents it hosts (orchestrator, coding agent), every agent with its sub-agents, messages
// and commands into them, their token usage, the definitions with their graphs, the architect and the
// organisation. Every call goes through the admin API to the Core's admin door.
const API = '/api/admin/veridion-core';
const REFRESH_MS = 10000;
// While an answer from the dashboard's conversation with an agent is pending, its thread is read this often.
const PENDING_MS = 3000;
const ACTIVE = ['building', 'starting', 'running', 'waiting', 'idle', 'paused'];

const call = (path, body) => requestJson(`${API}${path}`, body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) });

const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());
const formatTime = formatBerlin;
const short = (value, length = 160) => {
  const text = typeof value === 'string' ? value : JSON.stringify(value ?? '');
  return text.length > length ? `${text.slice(0, length)}…` : text;
};

// A section of a view: the shared section with its state in the header (IDD D-HP-ADMIN-040).
const Section = ({ id = '', title, subtitle, children, actions = null, state = '', count = null, tone = '', collapsed = false }) => (
  <AdminSection id={id} title={title} subtitle={subtitle} actions={actions} state={state} count={count} tone={tone} collapsed={collapsed}>
    {children}
  </AdminSection>
);

const Notice = ({ state }) => (state?.message ? (
  <div className={`homepage-muted-panel p-3 text-sm ${state.error ? 'text-error' : 'text-text-secondary'}`}>{state.message}</div>
) : null);

const useAction = (t, onDone) => {
  const [state, setState] = useState({ busy: false, message: '', error: false });
  const run = async (action, doneMessage) => {
    setState({ busy: true, message: '', error: false });
    try {
      const result = await action();
      setState({ busy: false, message: doneMessage || t.admin.core.done, error: false });
      if (onDone) onDone(result);
      return result;
    } catch (error) {
      const code = error?.code || error?.status || '-';
      setState({ busy: false, message: `${t.admin.core.errors?.[code] || t.admin.core.failed} (${code})`, error: true });
      return null;
    }
  };
  return [state, run];
};

const statusLabel = (t, status) => t.admin.core.status?.[status] || status || '-';

// -- one agent, opened from the table ------------------------------------------------------------

// The dashboard's conversation with an agent (IDD/projects/veridion/core, D-VCORE-116 "Every Agent Can Be Written From
// The Dashboard, It Answers Where The Company Speaks"): every message written here with its answer, read and written
// through the door Veridion Core names for this agent (`messagesDoor`: an AI colleague's, the coding agent's or the
// agent's own), so the panel hardcodes none. Every message and answer is also said in Webex, in the thread of the
// agent's work or in the administrator's direct room with the bot.
const Conversation = ({ t, agent, thread, canWrite, graph, busy, onSend }) => {
  const [text, setText] = useState('');
  const [urgent, setUrgent] = useState(false);
  return (
    <div className="space-y-3">
      <h4 className="font-black">{t.admin.core.conversationTitle}</h4>
      <div className="text-sm space-y-2" style={{ maxHeight: '320px', overflowY: 'auto' }}>
        {thread.length ? thread.map((entry) => (
          <div key={entry.id} className="space-y-1">
            {entry.text ? (
              <div className="site-card p-2" style={{ whiteSpace: 'pre-wrap' }}>
                <div className="text-xs text-text-secondary">{formatTime(entry.created_at)} · {entry.sender} → {agent.title}</div>
                {entry.text}
              </div>
            ) : null}
            <div className="homepage-muted-panel p-2" style={{ whiteSpace: 'pre-wrap' }}>
              <div className="text-xs text-text-secondary">{entry.answered_at ? `${formatTime(entry.answered_at)} · ` : ''}{entry.text ? agent.title : entry.sender}</div>
              {entry.answer || <em>{t.admin.core.answerPending}</em>}
            </div>
          </div>
        )) : <div className="text-text-secondary">{t.admin.core.conversationEmpty}</div>}
      </div>
      {canWrite ? (
        <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); onSend(text, urgent).then((ok) => ok && setText('')); }}>
          <label className="block text-sm font-bold">{agent.waiting?.question ? t.admin.core.answerQuestion : t.admin.core.writeToAgent}</label>
          <textarea className="form-input w-full" rows={3} value={text} onChange={(event) => setText(event.target.value)} required />
          <div className="flex flex-wrap items-center gap-4">
            {graph ? <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={urgent} onChange={(event) => setUrgent(event.target.checked)} />{t.admin.core.urgent}</label> : null}
            <button type="submit" className="homepage-button-primary" disabled={busy}>{t.admin.core.send}</button>
          </div>
          <p className="text-xs text-text-secondary">{t.admin.core.conversationHint}</p>
        </form>
      ) : <div className="text-sm text-text-secondary">{t.admin.core.errors?.AGENT_FINISHED}</div>}
    </div>
  );
};

export const AgentDetail = ({ t, agentId, onChanged = () => {}, onClose, readOnly = false }) => {
  const [detail, setDetail] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState('');
  const [urgent, setUrgent] = useState(false);
  const load = useCallback(async () => {
    try {
      const data = await call(`/agents/${agentId}`);
      setDetail(data);
      if (data.messagesDoor) {
        try {
          setThread((await call(data.messagesDoor)).items || []);
        } catch {
          // The thread keeps what it showed.
        }
      }
    } catch (error) {
      setDetail({ error: error?.code || 'unavailable' });
    }
  }, [agentId]);
  const [state, run] = useAction(t, () => { load(); onChanged(); });
  const pending = thread.some((entry) => !entry.answered_at);

  useEffect(() => { load(); const timer = setInterval(load, pending ? PENDING_MS : REFRESH_MS); return () => clearInterval(timer); }, [load, pending]);

  if (!detail) return <div className="homepage-muted-panel p-4 text-sm">{t.admin.core.loading}</div>;
  if (detail.error) return <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.core.failed} ({detail.error})</div>;
  const agent = detail.agent;
  const active = ACTIVE.includes(agent.status) && !readOnly;
  const question = agent.waiting?.question;
  // Through its door every agent is written to, a built one while it runs; the orchestrator, which names no door,
  // takes a message into its mailbox as before.
  const door = detail.messagesDoor;
  const graph = agent.kind === 'graph';
  const canWrite = door ? (!graph || ACTIVE.includes(agent.status)) : active;

  return (
    <div className="homepage-muted-panel p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-black">{agent.title} <span className="text-sm font-normal text-text-secondary">({agent.id})</span></h3>
          <div className="text-sm text-text-secondary">
            {t.admin.core.statusLabel}: {statusLabel(t, agent.status)} · {t.admin.core.owner}: {agent.owner || '-'} · {t.admin.core.definition}: {agent.definition_id || '-'}{agent.version ? ` v${agent.version}` : ''}
            {agent.parent_id ? ` · ${t.admin.core.parent}: ${agent.parent_id}` : ''}
          </div>
          <div className="text-sm text-text-secondary">{t.admin.core.tokens}: {formatInteger(agent.used_tokens)}{agent.progress ? ` · ${t.admin.core.progress}: ${agent.progress}` : ''}</div>
        </div>
        <button type="button" className="homepage-button-secondary" onClick={onClose}>{t.admin.core.close}</button>
      </div>
      {question ? <div className="site-card p-3 text-sm"><strong>{t.admin.core.openQuestion}:</strong> {question}</div> : null}
      {agent.error ? <div className="text-sm text-error">{agent.error}</div> : null}
      {agent.result !== null && agent.result !== undefined && !active ? <div className="site-card p-3 text-sm" style={{ whiteSpace: 'pre-wrap' }}><strong>{t.admin.core.result}:</strong> {short(agent.result, 4000)}</div> : null}

      {door ? (
        <Conversation t={t} agent={agent} thread={thread} canWrite={canWrite} graph={graph} busy={state.busy}
          onSend={(message, urgentNow) => run(() => call(door, { text: message, urgent: urgentNow }), t.admin.core.messageSent)} />
      ) : active ? (
        <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); run(() => call(`/agents/${agent.id}/messages`, { text, urgent }), t.admin.core.messageSent).then((ok) => ok && setText('')); }}>
          <label className="block text-sm font-bold">{question ? t.admin.core.answerQuestion : t.admin.core.sendMessage}</label>
          <textarea className="form-input w-full" rows={3} value={text} onChange={(event) => setText(event.target.value)} required />
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={urgent} onChange={(event) => setUrgent(event.target.checked)} />{t.admin.core.urgent}</label>
            <button type="submit" className="homepage-button-primary" disabled={state.busy}>{t.admin.core.send}</button>
          </div>
        </form>
      ) : null}

      {agent.kind === 'graph' && active ? (
        <div className="flex flex-wrap gap-2">
          {['pause', 'resume', 'cancel', 'kill'].map((command) => (
            <button key={command} type="button" className={`homepage-button-secondary ${command === 'kill' || command === 'cancel' ? 'is-destructive' : ''}`} disabled={state.busy}
              onClick={() => (command === 'kill' && !window.confirm(t.admin.core.confirmKill) ? null : run(() => call(`/agents/${agent.id}/command`, { command })))}>
              {t.admin.core.commands[command]}
            </button>
          ))}
        </div>
      ) : null}

      {readOnly && !door ? <div className="text-sm text-text-secondary">{t.admin.ops?.positionMailboxHint}</div> : null}
      <Notice state={state} />

      {detail.children?.length ? (
        <div className="text-sm"><strong>{t.admin.core.subAgents}:</strong> {detail.children.map((child) => `${child.title} (${child.id}, ${statusLabel(t, child.status)})`).join(' · ')}</div>
      ) : null}
      {detail.pictureBase64 ? <><h4 className="font-black">{t.admin.core.graph}</h4><img src={`data:image/png;base64,${detail.pictureBase64}`} alt={t.admin.core.graph} style={{ maxWidth: '100%', background: '#fff' }} /></> : null}
      {detail.outline ? <pre className="text-xs" style={{ whiteSpace: 'pre-wrap' }}>{detail.outline}</pre> : null}

      <h4 className="font-black">{t.admin.core.messages}</h4>
      <div className="text-xs space-y-1" style={{ maxHeight: '220px', overflowY: 'auto' }}>
        {detail.messages?.length ? detail.messages.map((message) => (
          <div key={message.id}>{formatTime(message.created_at)} · {message.sender || '-'} · {message.kind}{message.urgent ? ' !' : ''} · {t.admin.core.messageStatus?.[message.status] || message.status}: {short(message.body?.text || message.body?.command || message.body, 300)}</div>
        )) : <div className="text-text-secondary">{t.admin.core.none}</div>}
      </div>
      <h4 className="font-black">{t.admin.core.events}</h4>
      <div className="text-xs space-y-1" style={{ maxHeight: '220px', overflowY: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}>
        {detail.events?.length ? detail.events.map((event, index) => (
          <div key={index}>{formatTime(event.created_at)} · {event.event_type}{event.tool_name ? ` · ${event.tool_name}` : ''}{event.detail ? ` · ${short(event.detail, 240)}` : ''}</div>
        )) : <div className="text-text-secondary">{t.admin.core.none}</div>}
      </div>
      {detail.log ? (
        <details><summary className="cursor-pointer text-sm font-bold">{t.admin.core.containerLog}</summary>
          <pre className="text-xs" style={{ whiteSpace: 'pre-wrap', maxHeight: '260px', overflowY: 'auto' }}>{detail.log}</pre>
        </details>
      ) : null}
    </div>
  );
};

// -- definitions -----------------------------------------------------------------------------------

const Definitions = ({ t, definitions, bots, onChanged }) => {
  const [selected, setSelected] = useState('');
  const [view, setView] = useState(null);
  const [input, setInput] = useState('{}');
  const [specText, setSpecText] = useState('');
  const [check, setCheck] = useState(null);
  const [state, run] = useAction(t, onChanged);

  useEffect(() => {
    if (!selected) { setView(null); return; }
    call(`/definitions/${selected}`).then(setView).catch(() => setView(null));
  }, [selected]);

  const parse = (value) => { try { return JSON.parse(value || '{}'); } catch { return null; } };

  return (
    <div className="space-y-4">
      <div className="admin-table-shell"><div className="admin-table-scroll">
        <table className="admin-table">
          <thead><tr><th>{t.admin.core.definition}</th><th>{t.admin.core.version}</th><th>{t.admin.core.builtBy}</th><th>{t.admin.core.designApprovedBy}</th><th>{t.admin.core.builtFor}</th><th>{t.admin.core.activeAgents}</th><th>{t.admin.core.updated}</th></tr></thead>
          <tbody>
            {definitions.map((definition) => (
              <tr key={definition.id} className="cursor-pointer" onClick={() => setSelected(definition.id)} style={selected === definition.id ? { fontWeight: 700 } : undefined}>
                <td>{definition.title} <span className="text-text-secondary">({definition.id}){definition.archived ? ` · ${t.admin.core.archived}` : ''}</span></td>
                <td>{definition.current_version}</td><td className="break-all">{builder(t, definition.origin, bots)}</td><td className="break-all">{definition.approved_by || '-'}</td><td>{definition.work_item || '-'}</td><td>{definition.active}</td><td>{formatTime(definition.updated_at)}</td>
              </tr>
            ))}
            {!definitions.length ? <tr><td colSpan="7">{t.admin.core.noDefinitions}</td></tr> : null}
          </tbody>
        </table>
      </div></div>

      {view ? (
        <div className="homepage-muted-panel p-4 space-y-3">
          <h3 className="text-lg font-black">{view.spec?.title} ({view.spec?.id})</h3>
          <p className="text-sm text-text-secondary">{view.spec?.description}</p>
          {view.pictureBase64 ? <img src={`data:image/png;base64,${view.pictureBase64}`} alt={t.admin.core.graph} style={{ maxWidth: '100%', background: '#fff' }} /> : null}
          <pre className="text-xs" style={{ whiteSpace: 'pre-wrap' }}>{view.outline}</pre>
          <div className="text-xs">{t.admin.core.versions}: {(view.versions || []).map((version) => `v${version.version} (${version.origin}, ${version.approved_by || '-'}, ${formatTime(version.created_at)}${version.parent_version ? `, ${t.admin.core.from} v${version.parent_version}` : ''})`).join(' · ')}</div>
          <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); const given = parse(input); if (given) run(() => call('/agents', { definition_id: view.spec.id, input: given }), t.admin.core.agentStarted); }}>
            <label className="block text-sm font-bold">{t.admin.core.startInput} ({(view.spec?.input || []).join(', ') || '-'})</label>
            <textarea className="form-input w-full font-mono text-xs" rows={3} value={input} onChange={(event) => setInput(event.target.value)} />
            <div className="flex flex-wrap items-end gap-2">
              <button type="submit" className="homepage-button-primary" disabled={state.busy}>{t.admin.core.startAgent}</button>
              <button type="button" className="homepage-button-secondary" onClick={() => setSpecText(JSON.stringify(view.spec, null, 2))}>{t.admin.core.editAsJson}</button>
              <button type="button" className="homepage-button-secondary is-destructive" disabled={state.busy} onClick={() => window.confirm(t.admin.core.confirmArchive) && run(() => call(`/definitions/${view.spec.id}/archive`, {}))}>{t.admin.core.archive}</button>
            </div>
          </form>
        </div>
      ) : null}

      <details className="homepage-muted-panel p-4">
        <summary className="cursor-pointer font-bold">{t.admin.core.definitionJson}</summary>
        <div className="space-y-2 mt-3">
          <p className="text-xs text-text-secondary">{t.admin.core.definitionJsonHint}</p>
          <textarea className="form-input w-full font-mono text-xs" rows={14} value={specText} onChange={(event) => setSpecText(event.target.value)} />
          <div className="flex flex-wrap gap-2">
            <button type="button" className="homepage-button-secondary" disabled={state.busy} onClick={async () => { const spec = parse(specText); if (spec) setCheck(await run(() => call('/definitions/validate', { spec }), t.admin.core.checked)); }}>{t.admin.core.validate}</button>
            <button type="button" className="homepage-button-primary" disabled={state.busy} onClick={() => { const spec = parse(specText); if (spec) run(() => call('/definitions', { spec }), t.admin.core.saved); }}>{t.admin.core.saveVersion}</button>
          </div>
          {check ? (
            <div className="space-y-2">
              <div className={`text-sm ${check.valid ? '' : 'text-error'}`}>{check.valid ? t.admin.core.valid : check.issues.map((issue) => `${issue.node ? `[${issue.node}] ` : ''}${issue.message}`).join(' · ')}</div>
              {check.pictureBase64 ? <img src={`data:image/png;base64,${check.pictureBase64}`} alt={t.admin.core.graph} style={{ maxWidth: '100%', background: '#fff' }} /> : null}
            </div>
          ) : null}
        </div>
      </details>
      <Notice state={state} />
    </div>
  );
};

// A direct chat with one vLLM engine or cloud model, past the bots, the agents and the lanes (IDD D-HP-ADMIN-048): the
// administrator picks a routed model and talks to it straight, grouped as vLLM / Audio / OpenRouter.
export const LlmConsole = ({ t }) => {
  const [data, setData] = useState(null);
  const [model, setModel] = useState('');
  const [system, setSystem] = useState('');
  const [prompt, setPrompt] = useState('');
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => call('/models').then((result) => { setData(result); setModel((current) => current || result.engine || (result.models || [])[0] || ''); }).catch(() => {}), []);
  const [state, run] = useAction(t, null);
  useEffect(() => { load(); }, [load]);
  const send = () => run(async () => {
    setError(''); setAnswer(null);
    try {
      setAnswer(await call('/llm/console', { model, system: system.trim() || undefined, prompt }));
    } catch (failure) { setError(failure?.message || failure?.code || failure?.status || 'unavailable'); }
  });
  const models = data?.models || [];
  const vllm = models.filter((name) => !name.includes('/') && !isAudioModel(name));
  const audio = models.filter((name) => isAudioModel(name));
  const cloud = models.filter((name) => name.includes('/') && !isAudioModel(name));
  const usage = answer?.usage;
  return (
    <div className="space-y-3">
      <p className="text-xs text-text-secondary">{t.admin.core.directLlmHint}</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-bold">{t.admin.core.directLlmModel}</span>
          <select className="form-input" value={model} onChange={(event) => setModel(event.target.value)} aria-label={t.admin.core.directLlmModel}>
            {vllm.length ? <optgroup label={t.admin.core.modelGroupVllm}>{vllm.map((name) => <option key={name} value={name}>{displayModelName(name)}</option>)}</optgroup> : null}
            {audio.length ? <optgroup label={t.admin.core.modelGroupAudio}>{audio.map((name) => <option key={name} value={name}>{displayModelName(name)}</option>)}</optgroup> : null}
            {cloud.length ? <optgroup label={t.admin.core.modelGroupCloud}>{cloud.map((name) => <option key={name} value={name}>{displayModelName(name)}</option>)}</optgroup> : null}
          </select>
        </label>
      </div>
      <label className="block text-sm">
        <span className="font-bold">{t.admin.core.directLlmSystem}</span>
        <textarea className="form-input mt-1 w-full" rows={2} value={system} onChange={(event) => setSystem(event.target.value)} placeholder={t.admin.core.directLlmSystemPlaceholder} />
      </label>
      <label className="block text-sm">
        <span className="font-bold">{t.admin.core.directLlmPrompt}</span>
        <textarea className="form-input mt-1 w-full" rows={4} value={prompt} onChange={(event) => setPrompt(event.target.value)} placeholder={t.admin.core.directLlmPromptPlaceholder} />
      </label>
      <div className="flex items-center gap-3">
        <button type="button" className="homepage-button-primary" disabled={state.busy || !model || !prompt.trim()} onClick={send}>{state.busy ? t.admin.core.directLlmSending : t.admin.core.directLlmSend}</button>
        {error ? <span className="text-sm text-error">{error}</span> : null}
      </div>
      {answer ? (
        <div className="homepage-muted-panel p-3 space-y-2">
          <div className="text-xs text-text-secondary">{displayModelName(answer.model)}{usage ? ` · ${String(t.admin.core.directLlmUsage || '').replace('{prompt}', usage.prompt).replace('{completion}', usage.completion).replace('{cost}', formatUsd(Number(usage.cost || 0)))}` : ''}</div>
          <pre className="whitespace-pre-wrap text-sm font-sans">{answer.content || '—'}</pre>
        </div>
      ) : null}
    </div>
  );
};

// -- the architect, from the dashboard -------------------------------------------------------------

const Architect = ({ t, onChanged }) => {
  const [text, setText] = useState('');
  const [log, setLog] = useState([]);
  const [isNew, setIsNew] = useState(true);
  const [graph, setGraph] = useState('');
  const [state, run] = useAction(t, onChanged);

  const send = async (event) => {
    event.preventDefault();
    const said = text;
    const result = await run(() => call('/architect', { text: said, new: isNew }), ' ');
    if (result) {
      setLog((entries) => [...entries, { who: 'admin', text: said }, { who: 'architect', text: result.message }]);
      const pictureBlock = (result.blocks || []).find((block) => block.metadata?.format === 'png-base64');
      setGraph(pictureBlock ? pictureBlock.text : '');
      setText('');
      setIsNew(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2" style={{ maxHeight: '420px', overflowY: 'auto' }}>
        {log.map((entry, index) => (
          <div key={index} className={`p-3 text-sm ${entry.who === 'admin' ? 'site-card' : 'homepage-muted-panel'}`} style={{ whiteSpace: 'pre-wrap' }}>
            <strong>{entry.who === 'admin' ? t.admin.core.you : t.admin.core.architect}:</strong> {entry.text}
          </div>
        ))}
      </div>
      {graph ? <img src={`data:image/png;base64,${graph}`} alt={t.admin.core.graph} style={{ maxWidth: '100%', background: '#fff' }} /> : null}
      <form className="space-y-2" onSubmit={send}>
        <textarea className="form-input w-full" rows={3} value={text} onChange={(event) => setText(event.target.value)} placeholder={t.admin.core.architectPlaceholder} required />
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={isNew} onChange={(event) => setIsNew(event.target.checked)} />{t.admin.core.newDesign}</label>
          <button type="submit" className="homepage-button-primary" disabled={state.busy}>{state.busy ? t.admin.core.working : t.admin.core.send}</button>
        </div>
      </form>
      {state.error ? <Notice state={state} /> : null}
    </div>
  );
};

// -- the organisation --------------------------------------------------------------------------------

// The executive layer and the business units come from Veridion Core's organisation (positions.py) and are synchronised
// into the organisation table at every start (IDD D-VCORE-269, D-HP-ADMIN-042): the dashboard shows them and lets people
// add their own units and members beside them, but never removes a synced unit or an AI colleague from one.
const SYNCED_KINDS = ['executive', 'business_unit'];
const unitKind = (t, kind) => ({ executive: t.admin.core.unitKindExecutive, business_unit: t.admin.core.unitKindBusinessUnit }[kind] || t.admin.core.unitKinds?.[kind] || kind);

const Organisation = ({ t }) => {
  const [org, setOrg] = useState({ units: [], members: [] });
  const [unit, setUnit] = useState({ id: '', name: '', kind: 'department', parent_id: 'company' });
  const [member, setMember] = useState({ unit_id: 'company', member_kind: 'person', member_id: '', role: '' });
  const load = useCallback(() => call('/org').then(setOrg).catch(() => {}), []);
  const [state, run] = useAction(t, load);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      {org.units.some((entry) => SYNCED_KINDS.includes(entry.kind)) ? <p className="text-xs text-text-secondary">{t.admin.core.orgSynced}</p> : null}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="admin-org-tree">
          {org.units.map((entry) => (
            <div key={entry.id} className="admin-org-unit">
              <div className="admin-org-unit-head">
                <div className="admin-org-unit-name"><strong>{entry.name}</strong><span className="admin-org-unit-meta">{entry.id} · {unitKind(t, entry.kind)}{entry.parent_id ? ` · ${t.admin.core.partOf} ${entry.parent_id}` : ''}</span></div>
                {entry.id !== 'company' && !SYNCED_KINDS.includes(entry.kind) ? <button type="button" className="homepage-button-secondary is-destructive admin-org-remove" onClick={() => window.confirm(t.admin.core.confirmRemove) && run(() => call(`/org/units/${entry.id}/delete`, {}))}>{t.admin.core.remove}</button> : <span />}
              </div>
              <ul className="admin-org-members">
                {org.members.filter((m) => m.unit_id === entry.id).map((m) => (
                  <li key={`${m.member_kind}-${m.member_id}`} className="admin-org-member">
                    <span className="admin-org-member-name">{m.name || m.member_id}</span>
                    <span className="admin-org-member-role">{m.role || ''}</span>
                    {!SYNCED_KINDS.includes(entry.kind) || m.member_kind === 'person' ? <button type="button" className="homepage-button-secondary is-destructive admin-org-remove" title={t.admin.core.remove} aria-label={`${t.admin.core.remove}: ${m.name || m.member_id}`} onClick={() => window.confirm(fill(t.admin.nav?.confirmRemove, { what: m.name || m.member_id })) && run(() => call('/org/members', { ...m, remove: true }))}>×</button> : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <form className="homepage-muted-panel p-4 space-y-2" onSubmit={(event) => { event.preventDefault(); run(() => call('/org/units', unit)); }}>
            <h3 className="font-black">{t.admin.core.addUnit}</h3>
            <input className="form-input w-full" placeholder={t.admin.core.unitId} value={unit.id} onChange={(event) => setUnit({ ...unit, id: event.target.value })} required />
            <input className="form-input w-full" placeholder={t.admin.core.unitName} value={unit.name} onChange={(event) => setUnit({ ...unit, name: event.target.value })} required />
            <select className="form-input w-full" value={unit.kind} onChange={(event) => setUnit({ ...unit, kind: event.target.value })}>
              {['department', 'team', 'project'].map((kind) => <option key={kind} value={kind}>{t.admin.core.unitKinds?.[kind] || kind}</option>)}
            </select>
            <select className="form-input w-full" value={unit.parent_id} onChange={(event) => setUnit({ ...unit, parent_id: event.target.value })}>
              {org.units.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
            </select>
            <button type="submit" className="homepage-button-secondary" disabled={state.busy}>{t.admin.core.add}</button>
          </form>
          <form className="homepage-muted-panel p-4 space-y-2" onSubmit={(event) => { event.preventDefault(); run(() => call('/org/members', member)); }}>
            <h3 className="font-black">{t.admin.core.addMember}</h3>
            <select className="form-input w-full" value={member.unit_id} onChange={(event) => setMember({ ...member, unit_id: event.target.value })}>
              {org.units.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
            </select>
            <select className="form-input w-full" value={member.member_kind} onChange={(event) => setMember({ ...member, member_kind: event.target.value })}>
              {['person', 'agent', 'definition'].map((kind) => <option key={kind} value={kind}>{t.admin.core.memberKinds?.[kind] || kind}</option>)}
            </select>
            <input className="form-input w-full" placeholder={t.admin.core.memberId} value={member.member_id} onChange={(event) => setMember({ ...member, member_id: event.target.value })} required />
            <input className="form-input w-full" placeholder={t.admin.core.role} value={member.role} onChange={(event) => setMember({ ...member, role: event.target.value })} />
            <button type="submit" className="homepage-button-secondary" disabled={state.busy}>{t.admin.core.add}</button>
          </form>
        </div>
      </div>
      <Notice state={state} />
    </div>
  );
};

// -- the models by role: the people's choice of what the company's face, the phone, the architect and the judge think on
// (IDD D-VCORE-265, D-VCORE-266, D-HP-ADMIN-045). Only a routed model can be chosen; "" is the configuration's default.

const ROLES = ['coordinator', 'call', 'architect', 'requirements_judge', 'judge', 'ear', 'stt'];
const roleLabel = (t, role) => ({ coordinator: t.admin.core.roleCoordinator, call: t.admin.core.roleCall, architect: t.admin.core.roleArchitect, requirements_judge: t.admin.core.roleRequirementsJudge, judge: t.admin.core.roleJudge, ear: t.admin.core.roleEar, stt: t.admin.core.roleStt }[role] || role);

// The choices, grouped as the people think of them (IDD D-HP-ADMIN-047): the company's own vLLM engines, the local audio
// vLLM, and the OpenRouter cloud models. A name with a vendor prefix is a cloud model; an audio one is grouped apart.
const isAudioModel = (name) => /asr|tts|audio/i.test(name);
const ModelChoices = ({ t, models, value, onChange, label }) => {
  const vllm = models.filter((name) => !name.includes('/') && !isAudioModel(name));
  const audio = models.filter((name) => isAudioModel(name));
  const cloud = models.filter((name) => name.includes('/') && !isAudioModel(name));
  return (
    <select className="form-input" value={value} onChange={onChange} aria-label={label}>
      <option value="">{t.admin.core.modelDefaultOption}</option>
      {vllm.length ? <optgroup label={t.admin.core.modelGroupVllm}>{vllm.map((name) => <option key={name} value={name}>{displayModelName(name)}</option>)}</optgroup> : null}
      {audio.length ? <optgroup label={t.admin.core.modelGroupAudio}>{audio.map((name) => <option key={name} value={name}>{displayModelName(name)}</option>)}</optgroup> : null}
      {cloud.length ? <optgroup label={t.admin.core.modelGroupCloud}>{cloud.map((name) => <option key={name} value={name}>{displayModelName(name)}</option>)}</optgroup> : null}
    </select>
  );
};

const ModelsByRole = ({ t }) => {
  const [data, setData] = useState(null);
  const [chosen, setChosen] = useState({});
  const [error, setError] = useState('');
  const load = useCallback(() => call('/models').then((result) => { setData(result); setChosen(Object.fromEntries(ROLES.map((role) => [role, result.roles?.[role]?.chosen || '']))); setError(''); }).catch((failure) => setError(failure?.code || failure?.status || 'unavailable')), []);
  const [state, run] = useAction(t, load);
  useEffect(() => { load(); }, [load]);
  if (error && !data) return <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.core.unavailable} ({error})</div>;
  if (!data) return <div className="homepage-muted-panel p-4 text-sm">{t.admin.core.loading || '…'}</div>;
  const models = data.models || [];
  return (
    <div className="space-y-3">
      <p className="text-xs text-text-secondary">{t.admin.core.engineSilver}: {displayModelName(data.engine) || '-'}{data.gold ? ` · ${t.admin.core.engineGold}: ${displayModelName(data.gold)}` : ''}</p>
      <div className="admin-table-shell"><div className="admin-table-scroll">
        <table className="admin-table">
          <thead><tr><th>{t.admin.core.role}</th><th>{t.admin.core.modelCurrent}</th><th>{t.admin.core.modelDefault}</th><th>{t.admin.core.modelChosen}</th><th /></tr></thead>
          <tbody>
            {ROLES.map((role) => {
              const entry = data.roles?.[role] || {};
              return (
                <tr key={role}>
                  <td><strong>{roleLabel(t, role)}</strong></td>
                  <td>{displayModelName(entry.model) || `${t.admin.core.engineSilver}`}{entry.model && entry.routed === false ? <div className="text-xs text-error">{t.admin.core.modelNotRouted}</div> : null}</td>
                  <td className="text-xs">{displayModelName(entry.default) || '-'}</td>
                  <td>
                    <ModelChoices t={t} models={models} value={chosen[role] ?? ''} onChange={(event) => setChosen({ ...chosen, [role]: event.target.value })} label={`${roleLabel(t, role)}: ${t.admin.core.modelChosen}`} />
                  </td>
                  <td><button type="button" className="homepage-button-secondary" disabled={state.busy || (chosen[role] ?? '') === (entry.chosen || '')} onClick={() => run(() => call('/models', { role, model: chosen[role] || '' }))}>{t.admin.core.modelSave}</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div></div>
      <Notice state={state} />
    </div>
  );
};

// -- the phone's calls, booked apart from the colleagues' work (IDD D-VCORE-267, D-HP-ADMIN-043) ------------------------

const CallTotals = ({ t, bots, rows, withTimes = true }) => (
  <div className="admin-table-shell"><div className="admin-table-scroll">
    <table className="admin-table">
      <thead><tr><th>{t.admin.org?.bot}</th><th>{t.admin.ops?.model}</th><th>{t.admin.core.callsCalls}</th><th>{t.admin.core.callsTurns}</th><th>{t.admin.ops?.promptTokens}</th><th>{t.admin.ops?.completionTokens}</th><th>{t.admin.ops?.cost}</th><th>{t.admin.core.callsAvgTotal}</th>{withTimes ? <><th>{t.admin.core.callsAvgAsr}</th><th>{t.admin.core.callsAvgLlm}</th><th>{t.admin.core.callsAvgTts}</th><th>{t.admin.core.callsDeferred}</th></> : null}</tr></thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.position}-${row.model}`}>
            <td><strong>{bots[row.position] || row.position}</strong></td>
            <td className="text-xs">{displayModelName(row.model)}</td>
            <td>{formatInteger(row.calls)}</td>
            <td>{formatInteger(row.turns)}</td>
            <td>{formatInteger(row.prompt_tokens)}</td>
            <td>{formatInteger(row.completion_tokens)}</td>
            <td>{formatUsd(Number(row.cost || 0))}</td>
            <td>{formatInteger(Math.round(Number(row.avg_total_ms || 0)))}</td>
            {withTimes ? <><td>{formatInteger(Math.round(Number(row.avg_asr_ms || 0)))}</td><td>{formatInteger(Math.round(Number(row.avg_llm_ms || 0)))}</td><td>{formatInteger(Math.round(Number(row.avg_tts_ms || 0)))}</td><td>{formatInteger(row.deferred)}</td></> : null}
          </tr>
        ))}
        {!rows.length ? <tr><td colSpan={withTimes ? 12 : 8}>{t.admin.core.callsNone}</td></tr> : null}
      </tbody>
    </table>
  </div></div>
);

const PhoneCalls = ({ t, bots }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => call('/calls?days=30').then((result) => { setData(result); setError(''); }).catch((failure) => setError(failure?.code || failure?.status || 'unavailable')), []);
  useEffect(() => { load(); const timer = setInterval(load, REFRESH_MS); return () => clearInterval(timer); }, [load]);
  if (error && !data) return <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.core.unavailable} ({error})</div>;
  if (!data) return <div className="homepage-muted-panel p-4 text-sm">{t.admin.core.loading || '…'}</div>;
  return (
    <div className="space-y-4">
      <h3 className="font-black">{t.admin.core.callsWindow}</h3>
      <CallTotals t={t} bots={bots} rows={data.totals || []} />
      <h3 className="font-black">{t.admin.core.callsToday}</h3>
      <CallTotals t={t} bots={bots} rows={data.today || []} withTimes={false} />
      <h3 className="font-black">{t.admin.core.callsRecent}</h3>
      <div className="admin-table-shell"><div className="admin-table-scroll">
        <table className="admin-table">
          <thead><tr><th>{t.admin.ops?.time}</th><th>{t.admin.org?.bot}</th><th>{t.admin.core.callsPerson}</th><th>{t.admin.ops?.model}</th><th>{t.admin.ops?.promptTokens}</th><th>{t.admin.ops?.completionTokens}</th><th>{t.admin.ops?.cost}</th><th>{t.admin.core.callsAvgAsr}</th><th>{t.admin.core.callsAvgLlm}</th><th>{t.admin.core.callsAvgTts}</th><th>{t.admin.core.callsAvgTotal}</th><th>{t.admin.core.callsRounds}</th><th>{t.admin.core.callsDeferred}</th></tr></thead>
          <tbody>
            {(data.recent || []).map((row) => (
              <tr key={row.id}>
                <td>{formatTime(row.created_at)}</td>
                <td><strong>{bots[row.position] || row.position}</strong></td>
                <td className="break-all text-xs">{row.person || '-'}</td>
                <td className="text-xs">{displayModelName(row.model)}</td>
                <td>{formatInteger(row.prompt_tokens)}</td>
                <td>{formatInteger(row.completion_tokens)}</td>
                <td>{formatUsd(Number(row.cost || 0))}</td>
                <td>{formatInteger(row.asr_ms)}</td>
                <td>{formatInteger(row.llm_ms)}</td>
                <td>{formatInteger(row.tts_ms)}</td>
                <td>{formatInteger(row.total_ms)}</td>
                <td>{formatInteger(row.rounds)}</td>
                <td>{formatInteger(row.deferred)}</td>
              </tr>
            ))}
            {!(data.recent || []).length ? <tr><td colSpan="13">{t.admin.core.callsNone}</td></tr> : null}
          </tbody>
        </table>
      </div></div>
    </div>
  );
};

// -- the Core's own records: builds, the tools it offers its agents, its usage and its activity ------

const Builds = ({ t, builds }) => {
  const o = t.admin.ops || {};
  return (
    <div className="admin-table-shell"><div className="admin-table-scroll">
      <table className="admin-table admin-fixed-table">
        <colgroup><col style={{ width: '18%' }} /><col style={{ width: '30%' }} /><col style={{ width: '16%' }} /><col style={{ width: '12%' }} /><col style={{ width: '12%' }} /><col style={{ width: '12%' }} /></colgroup>
        <thead><tr><th>{t.admin.core.agent}</th><th>{o.image}</th><th>{t.admin.core.owner}</th><th>{t.admin.core.statusLabel}</th><th>{o.started}</th><th>{o.finished}</th></tr></thead>
        <tbody>
          {builds.map((build, index) => (
            <tr key={`${build.agent_id}-${index}`}>
              <td className="admin-cell-clip" title={build.agent_id}>{build.agent_id}</td>
              <td className="admin-cell-clip" title={build.image}>{build.image || '-'}</td>
              <td className="admin-cell-clip" title={build.owner}>{build.owner || '-'}</td>
              <td>{statusLabel(t, build.status)}</td>
              <td>{formatTime(build.started_at || build.created_at)}</td>
              <td>{formatTime(build.finished_at)}</td>
            </tr>
          ))}
          {!builds.length ? <tr><td colSpan="6">{o.noBuilds}</td></tr> : null}
        </tbody>
      </table>
    </div></div>
  );
};

const CoreTools = ({ t, tools }) => {
  const o = t.admin.ops || {};
  return (
    <div className="admin-table-shell"><div className="admin-table-scroll">
      <table className="admin-table admin-fixed-table">
        <colgroup><col style={{ width: '30%' }} /><col style={{ width: '18%' }} /><col style={{ width: '12%' }} /><col style={{ width: '40%' }} /></colgroup>
        <thead><tr><th>{t.admin.veridionToolName}</th><th>{o.toolServer}</th><th>{o.toolAccess}</th><th>{o.description}</th></tr></thead>
        <tbody>
          {tools.map((tool) => (
            <tr key={tool.name}>
              <td className="admin-cell-clip" title={tool.name}>{tool.name}</td>
              <td className="admin-cell-clip" title={tool.server}>{tool.server || '-'}</td>
              <td><span className={`admin-status-pill ${tool.readOnly ? 'is-ok' : 'is-warning'}`}>{tool.readOnly ? o.accessReadOnly : o.accessWrites}</span></td>
              <td className="text-xs">{short(tool.description, 260)}</td>
            </tr>
          ))}
          {!tools.length ? <tr><td colSpan="4">{t.admin.veridionNoTools}</td></tr> : null}
        </tbody>
      </table>
    </div></div>
  );
};

// Where a model is served (D-HP-ADMIN-023): the gateway's map of model to source, handed in by the page.
const sourceOf = (sources, model) => ((sources || {})[model] || {}).source || '';
const sourceLabel = (t, source) => (source === 'gpu' ? t.admin.aiSourceGpu : source === 'openrouter' ? 'OpenRouter' : source === 'stackit' ? 'STACKIT' : source || '-');

// The colleagues' usage summed per agent and source: what each took from the GPU and from the cloud.
const CoreUsageBySource = ({ t, rows, sources }) => {
  const [shown, more] = useShowMore(30);  // paged like every long table (D-HP-ADMIN-040)
  const byAgent = {};
  rows.forEach((row) => {
    const entry = byAgent[row.agent_id] || (byAgent[row.agent_id] = { gpu: 0, cloud: 0, calls: 0, cost: 0 });
    const tokens = Number(row.tokens || 0);
    if (sourceOf(sources, row.model) === 'gpu') entry.gpu += tokens; else entry.cloud += tokens;
    entry.calls += Number(row.calls || 0);
    entry.cost += Number(row.cost || 0);
  });
  const agents = Object.entries(byAgent).sort((left, right) => (right[1].gpu + right[1].cloud) - (left[1].gpu + left[1].cloud));
  return (
    <>
    <div className="admin-table-shell"><div className="admin-table-scroll">
      <table className="admin-table admin-fixed-table">
        <colgroup><col style={{ width: '34%' }} /><col style={{ width: '12%' }} /><col style={{ width: '18%' }} /><col style={{ width: '18%' }} /><col style={{ width: '18%' }} /></colgroup>
        <thead><tr><th>{t.admin.core.agent}</th><th>{t.admin.core.calls}</th><th>{t.admin.core.gpuTokens}</th><th>{t.admin.core.cloudTokens}</th><th>{t.admin.core.cost}</th></tr></thead>
        <tbody>
          {agents.slice(0, shown).map(([agent, entry]) => (
            <tr key={agent}>
              <td className="admin-cell-clip" title={agent}>{agent}</td>
              <td>{formatInteger(entry.calls)}</td>
              <td>{formatInteger(entry.gpu)}</td>
              <td>{formatInteger(entry.cloud)}</td>
              <td>{entry.cost ? formatUsd(entry.cost) : '-'}</td>
            </tr>
          ))}
          {!agents.length ? <tr><td colSpan="5">{t.admin.core.none}</td></tr> : null}
        </tbody>
      </table>
    </div></div>
    <ShowMore shown={shown} total={agents.length} onMore={more} />
    </>
  );
};

// The newest fifty rows stand on the page, the rest on request (IDD D-HP-ADMIN-040).
const CoreUsageDaily = ({ t, rows, sources }) => {
  const o = t.admin.ops || {};
  const [shown, more] = useShowMore(50);
  return (
    <div className="space-y-2">
    <div className="admin-table-shell"><div className="admin-table-scroll">
      <table className="admin-table admin-fixed-table">
        <colgroup><col style={{ width: '11%' }} /><col style={{ width: '20%' }} /><col style={{ width: '23%' }} /><col style={{ width: '12%' }} /><col style={{ width: '9%' }} /><col style={{ width: '13%' }} /><col style={{ width: '12%' }} /></colgroup>
        <thead><tr><th>{o.day}</th><th>{t.admin.core.agent}</th><th>{o.model}</th><th>{t.admin.core.source}</th><th>{t.admin.core.calls}</th><th>{t.admin.core.tokens}</th><th>{t.admin.core.cost}</th></tr></thead>
        <tbody>
          {rows.slice(0, shown).map((row, index) => (
            <tr key={`${row.day}-${row.agent_id}-${row.model}-${index}`}>
              <td>{String(row.day || '').slice(0, 10)}</td>
              <td className="admin-cell-clip" title={row.agent_id}>{row.agent_id}</td>
              <td className="admin-cell-clip" title={displayModelName(row.model)}>{displayModelName(row.model) || '-'}</td>
              <td>{sourceLabel(t, sourceOf(sources, row.model))}</td>
              <td>{formatInteger(row.calls)}</td>
              <td>{formatInteger(Number(row.prompt_tokens || 0) + Number(row.completion_tokens || 0))}</td>
              <td>{row.cost ? formatUsd(row.cost) : '-'}</td>
            </tr>
          ))}
          {!rows.length ? <tr><td colSpan="7">{t.admin.core.none}</td></tr> : null}
        </tbody>
      </table>
    </div></div>
    <ShowMore shown={shown} total={rows.length} onMore={more} />
    </div>
  );
};

const CoreActivity = ({ t, items }) => {
  const o = t.admin.ops || {};
  const [shown, more] = useShowMore(50);
  return (
    <div className="space-y-2">
    <div className="admin-table-shell"><div className="admin-table-scroll">
      <table className="admin-table admin-fixed-table">
        <colgroup><col style={{ width: '14%' }} /><col style={{ width: '16%' }} /><col style={{ width: '16%' }} /><col style={{ width: '10%' }} /><col style={{ width: '14%' }} /><col style={{ width: '30%' }} /></colgroup>
        <thead><tr><th>{o.time}</th><th>{o.event}</th><th>{t.admin.core.agent}</th><th>{t.admin.core.statusLabel}</th><th>{o.tool}</th><th>{o.detail}</th></tr></thead>
        <tbody>
          {items.slice(0, shown).map((item) => (
            <tr key={item.id}>
              <td>{formatTime(item.timestamp)}</td>
              <td className="admin-cell-clip" title={item.eventType}>{item.eventType}</td>
              <td className="admin-cell-clip" title={item.agentId || ''}>{item.agentId || '-'}</td>
              <td>{item.status || '-'}</td>
              <td className="admin-cell-clip" title={item.toolName || ''}>{item.toolName || '-'}</td>
              <td className="text-xs">{short(item.summary || '', 300)}</td>
            </tr>
          ))}
          {!items.length ? <tr><td colSpan="6">{t.admin.core.none}</td></tr> : null}
        </tbody>
      </table>
    </div></div>
    <ShowMore shown={shown} total={items.length} onMore={more} />
    </div>
  );
};

// The bots of the positions as Veridion Core names them, for the lineage of an agent: who built it.
const usePositionBots = () => {
  const [bots, setBots] = useState({});
  useEffect(() => {
    call('/organisation')
      .then((data) => setBots(Object.fromEntries((data.positions || []).map((position) => [position.key, position.bot]))))
      .catch(() => setBots({}));
  }, []);
  return bots;
};
const builder = (t, origin, bots) => {
  const key = String(origin || '').startsWith('position:') ? String(origin).slice(9) : '';
  return key ? (bots[key] || key) : t.admin.core.designByPerson;
};

// -- paging and filters --------------------------------------------------------------------------------

// The lists grow without end; the server filters and pages them, and the dashboard shows one page at a time.
const Pager = ({ t, page, more, onPage }) => (page > 0 || more ? (
  <div className="admin-user-pager">
    <button type="button" className="homepage-button-secondary" disabled={page === 0} onClick={() => onPage(page - 1)}>{t.admin.core.pagePrev}</button>
    <span>{t.admin.core.pageLabel} {page + 1}</span>
    <button type="button" className="homepage-button-secondary" disabled={!more} onClick={() => onPage(page + 1)}>{t.admin.core.pageNext}</button>
  </div>
) : null);

const Choice = ({ value, options, onChange, label }) => (
  <label className="flex items-center gap-2 text-sm">{label}
    <select className="form-input admin-inline-input" value={value} onChange={(event) => onChange(event.target.value)}>
      {options.map(([key, text]) => <option key={key} value={key}>{text}</option>)}
    </select>
  </label>
);

// -- designs -----------------------------------------------------------------------------------------

// Every agent the architect designed, for a person or for a position's work, with its whole record: what for and
// why, every question with its answer and who gave it, the test runs against the scenarios, the approval and the
// runs (IDD/projects/veridion/core, D-VCORE-092 "Every Design Is Audited").
const DESIGN_PAGE = 20;
const AGENT_PAGE = 25;

const Designs = ({ t, bots }) => {
  const [items, setItems] = useState([]);
  const [more, setMore] = useState(false);
  const [page, setPage] = useState(0);
  const [stateFilter, setStateFilter] = useState('');
  const [position, setPosition] = useState('');
  const [selected, setSelected] = useState('');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const query = `state=${stateFilter}&position=${position}&limit=${DESIGN_PAGE}&offset=${page * DESIGN_PAGE}`;
    const load = () => call(`/designs?${query}`).then((data) => { setItems(data.items || []); setMore(Boolean(data.more)); })
      .catch(() => setItems([]));
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [stateFilter, position, page]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    call(`/designs/${selected}`).then(setDetail).catch(() => setDetail(null));
  }, [selected, items]);

  const stateOf = (status) => t.admin.core.designStatus?.[status] || status;
  const filter = (set) => (value) => { set(value); setPage(0); };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <Choice value={stateFilter} onChange={filter(setStateFilter)} label={t.admin.core.filterState}
          options={[['', t.admin.core.filterAll], ['open', t.admin.core.filterOpen], ['done', t.admin.core.filterDone], ['failed', t.admin.core.filterFailed]]} />
        <Choice value={position} onChange={filter(setPosition)} label={t.admin.core.filterPosition}
          options={[['', t.admin.core.filterAll], ...Object.entries(bots)]} />
        <Pager t={t} page={page} more={more} onPage={setPage} />
      </div>
      <div className="admin-table-shell"><div className="admin-table-scroll">
        <table className="admin-table">
          <thead><tr><th>{t.admin.core.design}</th><th>{t.admin.core.designFor}</th><th>{t.admin.core.designWork}</th><th>{t.admin.core.designState}</th><th>{t.admin.core.designApprovedBy}</th><th>{t.admin.core.designAgent}</th><th>{t.admin.core.updated}</th></tr></thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="cursor-pointer" onClick={() => setSelected(item.id)} style={selected === item.id ? { fontWeight: 700 } : undefined}>
                <td>{short(item.request, 90)}<div className="text-xs text-text-secondary">{item.id}{item.definition_id ? ` · ${item.definition_id}${item.version ? ` v${item.version}` : ''}` : ''}</div></td>
                <td className="break-all">{item.position ? (bots[item.position] || item.position) : t.admin.core.designByPerson}{item.task_id ? ` · ${t.admin.core.viaCodingTask}` : ''}<div className="text-xs text-text-secondary">{item.requester} · {t.admin.core.designTests}: {formatInteger(item.tests)} · {t.admin.core.designRuns}: {formatInteger(item.runs)}</div></td>
                <td>{item.work_item || '-'}</td>
                <td>{stateOf(item.status)}</td>
                <td className="break-all">{item.approved_by || '-'}</td>
                <td>{item.agent_title ? `${item.agent_title} · ${statusLabel(t, item.agent_status)}` : '-'}</td>
                <td>{formatTime(item.updated_at)}</td>
              </tr>
            ))}
            {!items.length ? <tr><td colSpan="7">{t.admin.core.noDesigns}</td></tr> : null}
          </tbody>
        </table>
      </div></div>

      {detail ? (
        <div className="homepage-muted-panel p-4 space-y-3">
          <h3 className="text-lg font-black">{short(detail.request, 160)}</h3>
          {detail.reason ? <p className="text-sm"><strong>{t.admin.core.designReason}:</strong> {detail.reason}</p> : null}
          {detail.pictureBase64 ? <><h4 className="font-bold">{t.admin.core.graph}</h4><img src={`data:image/png;base64,${detail.pictureBase64}`} alt={t.admin.core.graph} style={{ maxWidth: '100%', background: '#fff' }} /></> : null}
          <div>
            <h4 className="font-bold">{t.admin.core.designRecord}</h4>
            <ul className="space-y-2 text-sm">
              {(detail.record || []).map((entry, index) => (
                <li key={index}>
                  <span className="text-text-secondary">{entry.at} · {entry.kind}{entry.by ? ` · ${entry.by}` : ''}</span>
                  <pre className="text-xs" style={{ whiteSpace: 'pre-wrap' }}>{entry.text}</pre>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-bold">{t.admin.core.designAgents}</h4>
            <ul className="space-y-1 text-sm">
              {(detail.agents || []).map((agent) => (
                <li key={agent.id}>{agent.test ? t.admin.core.designTestRun : t.admin.core.designRun}: {agent.title} · {statusLabel(t, agent.status)} · {formatInteger(agent.used_tokens)} {t.admin.core.tokens}{agent.error ? ` · ${short(agent.error, 160)}` : ''}</li>
              ))}
              {!(detail.agents || []).length ? <li>{t.admin.core.none}</li> : null}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// -- diagrams ----------------------------------------------------------------------------------------

// The company's diagrams (veridion/mcp-orchestrator/diagrams): who drew which picture, the one it extends, the
// picture itself and its Mermaid code, which agents reuse.
const DIAGRAM_PAGE = 20;

const Diagrams = ({ t }) => {
  const [items, setItems] = useState([]);
  const [shown, setShown] = useState(DIAGRAM_PAGE);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState('');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const load = () => call(`/diagrams?q=${encodeURIComponent(query)}&limit=${shown + 1}`)
      .then((data) => setItems(data.items || [])).catch(() => setItems([]));
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [query, shown]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    call(`/diagrams/${selected}`).then(setDetail).catch(() => setDetail(null));
  }, [selected]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm">{t.admin.core.searchLabel}
          <input className="form-input admin-inline-input" type="search" value={query} onChange={(event) => { setQuery(event.target.value); setShown(DIAGRAM_PAGE); }} /></label>
        {items.length > shown ? <button type="button" className="homepage-button-secondary" onClick={() => setShown(shown + DIAGRAM_PAGE)}>{t.admin.core.showMore}</button> : null}
      </div>
      <div className="admin-table-shell"><div className="admin-table-scroll">
        <table className="admin-table">
          <thead><tr><th>{t.admin.core.diagramsTitle}</th><th>{t.admin.core.diagramAuthor}</th><th>{t.admin.core.diagramBasedOn}</th><th>{t.admin.core.updated}</th></tr></thead>
          <tbody>
            {items.slice(0, shown).map((item) => (
              <tr key={item.diagramId} className="cursor-pointer" onClick={() => setSelected(item.diagramId)} style={selected === item.diagramId ? { fontWeight: 700 } : undefined}>
                <td>{item.title}<div className="text-xs text-text-secondary">{item.diagramId}</div></td>
                <td className="break-all">{item.author || '-'}</td><td>{item.basedOn || '-'}</td><td>{formatTime(item.created)}</td>
              </tr>
            ))}
            {!items.length ? <tr><td colSpan="4">{t.admin.core.noDiagrams}</td></tr> : null}
          </tbody>
        </table>
      </div></div>
      {detail ? (
        <div className="homepage-muted-panel p-4 space-y-3">
          <h3 className="text-lg font-black">{detail.title}</h3>
          {detail.pictureBase64 ? <img src={`data:image/png;base64,${detail.pictureBase64}`} alt={detail.title} style={{ maxWidth: '100%', background: '#fff' }} /> : null}
          <details><summary className="text-sm font-bold cursor-pointer">{t.admin.core.diagramCode}</summary>
            <pre className="text-xs" style={{ whiteSpace: 'pre-wrap' }}>{detail.code}</pre></details>
        </div>
      ) : null}
    </div>
  );
};

// -- the panel -------------------------------------------------------------------------------------

// One view of Veridion Core at a time: `status`, `agents`, `designs`, `definitions`, `organisation` (with the
// sub-page in `section`), `usage`, `builds` or `activity`. Each view reads only what it shows and refreshes it while it is open. `company` is the
// overview of the company instance, from which the organisation view takes the bots' names in Webex and
// the connected systems; `sources` is the gateway's map of model to source for the usage view.
// The usage per agent and model, paged like every long table (IDD D-HP-ADMIN-040).
const UsageByModelTable = ({ t, o, usage, sources }) => {
  const [shown, more] = useShowMore(30);
  return (
    <>
      <h4 className="font-black mt-4">{t.admin.core.usageByModel}</h4>
      <div className="admin-table-shell"><div className="admin-table-scroll">
        <table className="admin-table admin-fixed-table">
          <colgroup><col style={{ width: '30%' }} /><col style={{ width: '28%' }} /><col style={{ width: '12%' }} /><col style={{ width: '10%' }} /><col style={{ width: '10%' }} /><col style={{ width: '10%' }} /></colgroup>
          <thead><tr><th>{t.admin.core.agent}</th><th>{o.model}</th><th>{t.admin.core.source}</th><th>{t.admin.core.calls}</th><th>{t.admin.core.tokens}</th><th>{t.admin.core.cost}</th></tr></thead>
          <tbody>
            {usage.slice(0, shown).map((entry) => (
              <tr key={`${entry.agent_id}-${entry.model}`}><td className="admin-cell-clip" title={entry.agent_id}>{entry.agent_id}</td><td className="admin-cell-clip" title={displayModelName(entry.model)}>{displayModelName(entry.model) || '-'}</td><td>{sourceLabel(t, sourceOf(sources, entry.model))}</td><td>{formatInteger(entry.calls)}</td><td>{formatInteger(entry.tokens)}</td><td>{entry.cost ? formatUsd(entry.cost) : '-'}</td></tr>
            ))}
            {!usage.length ? <tr><td colSpan="6">{t.admin.core.none}</td></tr> : null}
          </tbody>
        </table>
      </div></div>
      <ShowMore shown={shown} total={usage.length} onMore={more} />
    </>
  );
};

const VeridionCorePanel = ({ t, core, coreGateway, coreError, company = null, view = 'status', section = '', sources = null }) => {
  const [agents, setAgents] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [agentState, setAgentState] = useState('current');
  const [agentPage, setAgentPage] = useState(0);
  const [agentsMore, setAgentsMore] = useState(false);
  const [openAgent, setOpenAgent] = useState('');
  const [overview, setOverview] = useState(core);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [builds, setBuilds] = useState([]);
  const [tools, setTools] = useState([]);
  const [activity, setActivity] = useState([]);
  const bots = usePositionBots();

  const load = useCallback(async () => {
    const wants = {
      overview: ['status', 'agents', 'usage'].includes(view),
      agents: view === 'agents',
      definitions: view === 'definitions',
      daily: view === 'usage',
      builds: view === 'builds',
      activity: view === 'activity',
    };
    const [agentResult, definitionResult, overviewResult, dailyResult, buildResult, activityResult] = await Promise.allSettled([
      wants.agents ? call(`/agents?state=${agentState}&tests=false&limit=${AGENT_PAGE}&offset=${agentPage * AGENT_PAGE}`) : Promise.resolve(null),
      wants.definitions ? call('/definitions') : Promise.resolve(null),
      wants.overview ? call('/overview') : Promise.resolve(null),
      wants.daily ? call('/usage?days=30') : Promise.resolve(null),
      wants.builds ? call('/builds?limit=100') : Promise.resolve(null),
      wants.activity ? call('/activity?limit=200') : Promise.resolve(null),
    ]);
    if (agentResult.status === 'fulfilled' && agentResult.value) { setAgents(agentResult.value.items || []); setAgentsMore(Boolean(agentResult.value.more)); }
    if (definitionResult.status === 'fulfilled' && definitionResult.value) setDefinitions(definitionResult.value.items || []);
    if (overviewResult.status === 'fulfilled' && overviewResult.value) setOverview(overviewResult.value);
    if (dailyResult.status === 'fulfilled' && dailyResult.value) setDailyUsage(dailyResult.value.items || []);
    if (buildResult.status === 'fulfilled' && buildResult.value) setBuilds(buildResult.value.items || []);
    if (activityResult.status === 'fulfilled' && activityResult.value) setActivity(activityResult.value.items || []);
  }, [agentState, agentPage, view]);
  const [state, run] = useAction(t, load);

  useEffect(() => { load(); const timer = setInterval(load, REFRESH_MS); return () => clearInterval(timer); }, [load]);
  useEffect(() => { if (core) setOverview(core); }, [core]);
  // The tool catalogue asks the orchestrator afresh; it is read once when the view opens.
  useEffect(() => { if (view === 'builds') call('/tools').then((data) => setTools(data.items || [])).catch(() => setTools([])); }, [view]);

  const info = overview?.core || {};
  const hosted = overview?.hosted || [];
  const usage = overview?.usage || [];
  const counts = overview?.agents || {};
  const o = t.admin.ops || {};
  const byParent = {};
  agents.forEach((agent) => { (byParent[agent.parent_id || ''] = byParent[agent.parent_id || ''] || []).push(agent); });
  const rows = [];
  const walk = (parent, depth) => (byParent[parent] || []).forEach((agent) => { rows.push({ agent, depth }); walk(agent.id, depth + 1); });
  walk('', 0);
  agents.filter((agent) => agent.parent_id && !agents.some((other) => other.id === agent.parent_id)).forEach((agent) => rows.push({ agent, depth: 1 }));
  const graphRows = rows.filter((row) => row.agent.kind === 'graph');
  return (
    <>
      {view === 'status' ? (
        <>
      <Section id="core-status" title={t.admin.core.title} subtitle={t.admin.core.subtitle}
        state={[info.status || '-', info.emergencyStop ? t.admin.core.emergencyActive : '', info.autopilot ? t.admin.core.autopilotActive : ''].filter(Boolean).join(' · ')}
        tone={coreError || info.emergencyStop ? 'error' : info.status === 'ok' ? 'ok' : 'warning'}
        actions={(
          <div className="admin-inline-actions">
            <AdminLink tab="agents" section="agents">{t.admin.core.agentsTitle}</AdminLink>
            <AdminLink tab="agents" section="definitions">{t.admin.core.definitionsTitle}</AdminLink>
            <AdminLink tab="usage" section="company">{t.admin.core.gatewayTokens}</AdminLink>
          </div>
        )}>
        {coreError ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.core.unavailable} ({coreError})</div> : null}
        <FactGrid items={[
          [t.admin.core.statusLabel, info.status || '-'],
          [t.admin.core.model, displayModelName(info.model) || '-'],
          [t.admin.core.codingModel, displayModelName(info.codingModel) || '-'],
          [t.admin.core.memory, info.memory ? `${t.admin.core.ready}${info.graphiti ? ` · ${t.admin.core.factExtraction}` : ''}` : t.admin.core.notReady],
          [t.admin.core.cluster, info.kube ? t.admin.core.ready : t.admin.core.notReady],
          [t.admin.core.agentsRunning, formatInteger(ACTIVE.reduce((sum, key) => sum + (counts[key] || 0), 0))],
          [t.admin.core.agentsWaiting, formatInteger(counts.waiting || 0)],
          [t.admin.core.definitions, formatInteger(overview?.definitions)],
          [t.admin.core.limitsBuilds, info.limits?.buildsPerDay ? formatInteger(info.limits.buildsPerDay) : t.admin.aiBudgetUnlimited],
          [t.admin.core.limitsDesigns, info.limits?.designsPerDay ? formatInteger(info.limits.designsPerDay) : t.admin.aiBudgetUnlimited],
        ]} />
      </Section>

      <Section id="core-models" title={t.admin.core.modelsTitle} subtitle={t.admin.core.modelsSubtitle}>
        <ModelsByRole t={t} />
      </Section>
      <Section id="core-console" title={t.admin.core.directLlmTitle} subtitle={t.admin.core.directLlmSubtitle} collapsed>
        <LlmConsole t={t} />
      </Section>

      <Section id="core-controls" title={t.admin.nav?.controlsTitle}>
        <div className="admin-autopilot-master">
          <button type="button" className={info.autopilot ? 'homepage-button-primary' : 'homepage-button-secondary'} disabled={state.busy}
            onClick={() => window.confirm(info.autopilot ? t.admin.core.confirmAutopilotOff : t.admin.core.confirmAutopilotOn)
              && run(() => call('/autopilot', { on: !info.autopilot }))}>
            {info.autopilot ? t.admin.core.autopilotOff : t.admin.core.autopilotOn}
          </button>
          <button type="button" className={`${info.emergencyStop ? 'homepage-button-primary' : 'homepage-button-secondary'} is-destructive`} disabled={state.busy}
            onClick={() => window.confirm(info.emergencyStop ? t.admin.core.confirmEmergencyOff : t.admin.core.confirmEmergencyOn)
              && run(() => call('/emergency', { on: !info.emergencyStop }))}>
            {info.emergencyStop ? t.admin.core.emergencyRelease : t.admin.core.emergencyStop}
          </button>
        </div>
        {(info.autopilotUnits || []).length ? (
          <div className="homepage-muted-panel p-3 space-y-2 mt-3">
            <div className="text-xs font-bold">{t.admin.core.autopilotUnitsTitle}</div>
            <div className="text-xs text-text-secondary">{t.admin.core.autopilotUnitsHint}</div>
            <div className="admin-autopilot-grid">
              {(info.autopilotUnits || []).map((unit) => {
                const labelText = `${unit.name}${unit.prefix ? ` (${unit.prefix})` : ''}`;
                const stateText = `${unit.on ? t.admin.core.autopilotUnitOn : t.admin.core.autopilotUnitOff}${unit.status === 'standby' ? ` · ${t.admin.org?.standingStandby || 'standby'}` : ''}`;
                return (
                  <button key={unit.id} type="button"
                    className={`${unit.on ? 'homepage-button-primary' : 'homepage-button-secondary'} admin-autopilot-unit-btn`}
                    disabled={state.busy}
                    title={`${labelText} · ${stateText}`}
                    onClick={() => run(() => call('/autopilot', { unit: unit.id, on: !unit.on }))}>
                    <span className="admin-autopilot-unit-label">{labelText}</span>
                    <span className="admin-autopilot-unit-state">{stateText}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        <form className="admin-form-row" onSubmit={(event) => {
          event.preventDefault();
          const form = event.currentTarget;
          run(() => call('/limits', { buildsPerDay: Number(form.builds.value), designsPerDay: Number(form.designs.value) }));
        }}>
          <label className="admin-field">{t.admin.core.limitsBuilds}
            <input name="builds" className="form-input" type="number" min="0" defaultValue={info.limits?.buildsPerDay ?? ''} />
          </label>
          <label className="admin-field">{t.admin.core.limitsDesigns}
            <input name="designs" className="form-input" type="number" min="0" defaultValue={info.limits?.designsPerDay ?? ''} />
          </label>
          <button type="submit" className="homepage-button-secondary" disabled={state.busy}>{t.admin.core.limitsSave}</button>
          <span className="text-xs text-text-muted">{t.admin.core.limitsNone}</span>
        </form>
        <Notice state={state} />
      </Section>
        </>
      ) : null}

      {view === 'agents' ? (
        <>
      <Section id="core-agents" title={t.admin.core.agentsTitle} subtitle={t.admin.core.agentsSubtitle} count={graphRows.length}
        state={`${t.admin.core.agentsRunning}: ${formatInteger(ACTIVE.reduce((sum, key) => sum + (counts[key] || 0), 0))} · ${t.admin.core.agentsWaiting}: ${formatInteger(counts.waiting || 0)}`}
        tone={Number(counts.waiting) > 0 ? 'warning' : ''}
        actions={<div className="flex flex-wrap items-center gap-4">
          <Choice value={agentState} onChange={(value) => { setAgentState(value); setAgentPage(0); }} label={t.admin.core.filterState}
            options={[['current', t.admin.core.filterCurrent], ['active', t.admin.core.filterActive], ['failed', t.admin.core.filterFailed], ['finished', t.admin.core.filterFinished], ['', t.admin.core.filterAll]]} />
          <Pager t={t} page={agentPage} more={agentsMore} onPage={setAgentPage} /></div>}>
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table">
            <thead><tr><th>{t.admin.core.agent}</th><th>{t.admin.core.statusLabel}</th><th>{t.admin.core.owner}</th><th>{t.admin.core.tokens}</th><th>{t.admin.core.progress}</th><th>{t.admin.core.updated}</th></tr></thead>
            <tbody>
              {graphRows.map(({ agent, depth }) => (
                <tr key={agent.id} className="cursor-pointer" onClick={() => setOpenAgent(agent.id)}>
                  <td style={{ paddingLeft: `${0.75 + depth * 1.25}rem` }}>{depth ? '↳ ' : ''}{agent.title}<div className="text-xs text-text-secondary">{agent.id}{agent.definition_id ? ` · ${agent.definition_id} v${agent.version}` : ''}{agent.test ? ` · ${t.admin.core.designTestRun}` : ''}{agent.design_id ? ` · ${t.admin.core.builtBy} ${agent.position ? (bots[agent.position] || agent.position) : t.admin.core.designByPerson} (${agent.design_id})` : ''}</div></td>
                  <td>{statusLabel(t, agent.status)}</td>
                  <td className="break-all">{agent.owner || '-'}</td>
                  <td>{formatInteger(agent.used_tokens)}</td>
                  <td>{agent.waiting?.question ? `${t.admin.core.openQuestion}: ${short(agent.waiting.question, 80)}` : short(agent.progress || agent.error || '', 80)}</td>
                  <td>{formatTime(agent.updated_at)}</td>
                </tr>
              ))}
              {!graphRows.length ? <tr><td colSpan="6">{t.admin.core.noAgents}</td></tr> : null}
            </tbody>
          </table>
        </div></div>
        {openAgent ? <AgentDetail t={t} agentId={openAgent} onChanged={load} onClose={() => setOpenAgent('')} /> : null}
      </Section>

      <Section id="core-hosted" title={t.admin.core.hostedTitle} subtitle={t.admin.core.hostedSubtitle} count={hosted.length}
        tone={hosted.some((entry) => ['starting', 'not_deployed'].includes(entry.status)) ? 'error' : hosted.length ? 'ok' : ''}>
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table">
            <thead><tr><th>{t.admin.core.agent}</th><th>{t.admin.core.statusLabel}</th><th>{t.admin.core.tokens}</th><th>{t.admin.core.actions}</th></tr></thead>
            <tbody>
              {hosted.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.title}<div className="text-xs text-text-secondary">{entry.deployment} · {entry.protocol}</div></td>
                  <td>{statusLabel(t, entry.status)}{entry.deploymentState?.exists ? ` (${entry.deploymentState.ready}/${entry.deploymentState.replicas})` : ''}</td>
                  <td>{formatInteger(entry.used_tokens)}</td>
                  <td className="admin-inline-actions">
                    {['start', 'stop', 'restart'].map((action) => (
                      <button key={action} type="button" className={`homepage-button-secondary ${action === 'stop' ? 'is-destructive' : ''}`} disabled={state.busy}
                        onClick={() => window.confirm(t.admin.core.confirmHosted.replace('{action}', t.admin.core.hostedActions[action]).replace('{agent}', entry.title))
                          && run(() => call(`/hosted/${entry.id}/${action}`, {}))}>{t.admin.core.hostedActions[action]}</button>
                    ))}
                    <button type="button" className="homepage-button-secondary" onClick={() => setOpenAgent(entry.id)}>{t.admin.core.open}</button>
                  </td>
                </tr>
              ))}
              {!hosted.length ? <tr><td colSpan="4">{t.admin.core.none}</td></tr> : null}
            </tbody>
          </table>
        </div></div>
      </Section>
        </>
      ) : null}

      {view === 'designs' ? (
      <Section id="core-designs" title={t.admin.core.designsTitle} subtitle={t.admin.core.designsSubtitle}>
        <Designs t={t} bots={bots} />
      </Section>
      ) : null}

      {view === 'definitions' ? (
        <>
      <Section id="core-definitions" title={t.admin.core.definitionsTitle} subtitle={t.admin.core.definitionsSubtitle} count={definitions.length}>
        <Definitions t={t} definitions={definitions} bots={bots} onChanged={load} />
      </Section>

      <Section id="core-architect" title={t.admin.core.architectTitle} subtitle={t.admin.core.architectSubtitle} collapsed>
        <Architect t={t} onChanged={load} />
      </Section>

      <Section id="core-diagrams" title={t.admin.core.diagramsTitle} subtitle={t.admin.core.diagramsSubtitle} collapsed>
        <Diagrams t={t} />
      </Section>
        </>
      ) : null}

      {view === 'organisation' ? (
      <VeridionOrganisationPanel t={t} section={section} hosted={hosted} webexBots={company?.webex?.data?.bots || []}
        servers={company?.mcp_servers?.data?.items || []}
        renderAgent={(agentId, close) => <AgentDetail t={t} agentId={agentId} onClose={close} readOnly />}
        editor={(
          <Section id="core-org-edit" title={t.admin.core.orgTitle} subtitle={t.admin.core.orgSubtitle}>
            <Organisation t={t} />
          </Section>
        )} />
      ) : null}

      {view === 'usage' ? (
        <>
      <Section id="core-usage" title={t.admin.core.usageTitle} subtitle={t.admin.core.usageSubtitle} count={usage.length} collapsed>
        <CoreUsageBySource t={t} rows={usage} sources={sources} />
        <UsageByModelTable t={t} o={o} usage={usage} sources={sources} />
      </Section>

          <Section id="core-usage-daily" title={o.coreDailyTitle} subtitle={o.coreDailySubtitle} count={dailyUsage.length} collapsed>
            <CoreUsageDaily t={t} rows={dailyUsage} sources={sources} />
          </Section>

          <Section id="core-calls" title={t.admin.core.callsTitle} subtitle={t.admin.core.callsSubtitle}>
            <PhoneCalls t={t} bots={bots} />
          </Section>
        </>
      ) : null}

      {view === 'builds' ? (
        <>
          <Section id="core-builds" title={o.buildsTitle} subtitle={o.buildsSubtitle} count={builds.length}>
            <Builds t={t} builds={builds} />
          </Section>
          <Section id="core-tools" title={o.coreToolsTitle} subtitle={o.coreToolsSubtitle} count={tools.length} collapsed>
            <CoreTools t={t} tools={tools} />
          </Section>
        </>
      ) : null}

      {view === 'activity' ? (
        <Section id="core-activity" title={o.coreActivityTitle} subtitle={o.coreActivitySubtitle} count={activity.length}>
          <CoreActivity t={t} items={activity} />
        </Section>
      ) : null}
    </>
  );
};

export default VeridionCorePanel;
